import type { ScaleLinear, ScaleTime } from 'd3-scale';
import { isEmpty } from 'lodash-es';
import { type JSX, useCallback, useRef } from 'react';

import * as styles from './DataTableGanttChartHeader.sty.js';
import { useMinMaxFromConfig } from './hooks/useMinMaxFromConfig.js';
import { useScaleAndFormatter } from './hooks/useScaleAndFormatter.js';
import { toIntegrationTracks } from './to-integration-tracks.js';
import { AnnotationsNumericalDisplay } from '../../../../charts/core/components/annotations/components/displays/AnnotationsNumericalDisplay.js';
import { AnnotationsTimeDisplay } from '../../../../charts/core/components/annotations/components/displays/AnnotationsTimeDisplay.js';
import { AnnotationsScrollWrapper } from '../../../../charts/core/components/annotations/components/overlays/AnnotationsScrollWrapper.js';
import {
  TRACK_HEIGHT,
  TRACK_SEPARATOR_STROKE_WIDTH,
} from '../../../../charts/core/components/annotations/constants.js';
import type { SelectionState } from '../../../../charts/core/components/annotations/types/state.js';
import { TimeTableHeaderAxis } from '../../../../charts/core/components/axes/components/TimeTableHeaderAxis.js';
import { ValueTableHeaderAxis } from '../../../../charts/core/components/axes/components/ValueTableHeaderAxis.js';
import { ChartStateHandler } from '../../../../charts/core/components/chart-states/ChartStateHandler.js';
import type { TickValueFormatter } from '../../../../charts/core/types/tick-value.js';
import { coerceSizeValue } from '../../../../charts/core/utils/coerceSizeValue.js';
import { useContainerSize } from '../../../../core/hooks/useContainerSize.js';
import { Flex } from '../../../../layouts/flex/Flex.js';
import { DataTableDefaultCell } from '../../components/DataTableDefaultCell.js';
import {
  DATA_TABLE_CELL_HORIZONTAL_PADDING_MAP as CELL_HORIZONTAL_PADDING_MAP,
  DATA_TABLE_DEFAULT_ICON_PADDING as ICON_PADDING,
  DATA_TABLE_SUB_ROW_EXPANDER_ICON_SIZE as SUB_ROW_EXPANDER_ICON_SIZE,
  DATA_TABLE_SUB_ROW_INDENT_GRID as SUB_ROW_INDENT_GRID,
} from '../../constants.js';
import { useDataTable } from '../../contexts/DataTableContext.js';
import type { DataTableDisplayColumnDef } from '../../public.api.js';
import type { DataTableGanttColumnConfig } from '../ColumnTypes/column-types-types.js';

type DataTableGanttChartHeaderProps<TData, TValue> = Readonly<{
  columnDef: DataTableDisplayColumnDef<TData, TValue>;
}>;

/**
 * @internal
 */
export const DataTableGanttChartHeader = <TData, TValue>({
  columnDef,
}: Readonly<DataTableGanttChartHeaderProps<TData, TValue>>): JSX.Element => {
  const sizeRef = useRef<HTMLDivElement>(null);
  const { width: axisWidth, height: axisHeight } = useContainerSize(sizeRef);
  const config = (columnDef.config || {}) as DataTableGanttColumnConfig;
  const { xAxisType = 'numerical', annotationsHeader } = config;
  const { min, max } = useMinMaxFromConfig(columnDef.id);

  const table = useDataTable();
  const isSubrowIndicatorColumn =
    table.getSubRowIndicatorColumnId() === columnDef.id;

  const leftHeaderPaddingValue = isSubrowIndicatorColumn
    ? SUB_ROW_INDENT_GRID * table.getSubRowOpenDepth()
    : 0;
  const leftHeaderPadding = isSubrowIndicatorColumn
    ? `${leftHeaderPaddingValue}px`
    : 'inherit';

  // this width is calculated from subrow expander icon size + the padding on both sides, and row density
  const subrowExpanderWidth = isSubrowIndicatorColumn
    ? SUB_ROW_EXPANDER_ICON_SIZE +
      2 * ICON_PADDING +
      CELL_HORIZONTAL_PADDING_MAP[
        table.atoms.visualVariantConfig?.get().rowDensity
      ]
    : 0;

  // we need to subtract the subrow expander size from the axis width
  const effectiveAxisWidth = Math.max(
    0,
    axisWidth - leftHeaderPaddingValue - subrowExpanderWidth,
  );

  const { scale, formatter } = useScaleAndFormatter(
    effectiveAxisWidth,
    config,
    min,
    max,
  );

  const {
    tooltip: annotationsTooltip,
    tooltipActions: annotationsTooltipActions,
    emptyState,
    errorState,
    loading,
    textOverflow = 'truncate',
    truncateMode = 'middle',
    tracks: annotationTracksConfig,
    height: annotationsHeightConfig,
  } = annotationsHeader ?? {};

  const tooltipConfig = annotationsTooltip
    ? { template: annotationsTooltip }
    : {};

  const columnId = columnDef.id;
  const setGanttAnnotationsSelection = table.setGanttAnnotationsSelection;
  const handleMarkerGroupHover = useCallback(
    (event: Partial<SelectionState>) => {
      setGanttAnnotationsSelection(columnId, event);
    },
    [setGanttAnnotationsSelection, columnId],
  );

  const annotationTracks = annotationTracksConfig
    ? toIntegrationTracks(annotationTracksConfig)
    : [];
  const shownTracks = annotationTracks.filter(({ hidden }) => !hidden);
  const tracksHeight =
    shownTracks.length * TRACK_HEIGHT + TRACK_SEPARATOR_STROKE_WIDTH;

  const configuredHeight = coerceSizeValue(annotationsHeightConfig);
  const scrollWrapperHeight = configuredHeight
    ? `min(${tracksHeight}px, ${configuredHeight})`
    : tracksHeight;

  let ganttAxis: JSX.Element = (
    <ValueTableHeaderAxis
      scale={scale as ScaleLinear<number, number>}
      formatter={formatter as TickValueFormatter}
      containerHeight={axisHeight}
      containerWidth={effectiveAxisWidth}
      min={min}
      max={max}
    />
  );
  let annotationsDisplay: JSX.Element = (
    <AnnotationsNumericalDisplay
      scale={scale as ScaleLinear<number, number>}
      tracks={annotationTracks}
      showLabels={false}
      labelsOffset={0}
      width={effectiveAxisWidth}
      markerGroupHover={handleMarkerGroupHover}
      tooltipConfig={tooltipConfig}
      annotationsActions={annotationsTooltipActions}
      textOverflow={textOverflow}
      truncateMode={truncateMode}
      formatter={formatter}
    />
  );

  // Get the correct axis components based on the xAxisType
  if (xAxisType === 'time') {
    ganttAxis = (
      <TimeTableHeaderAxis
        scale={scale as ScaleTime<number, number>}
        containerHeight={axisHeight}
        containerWidth={effectiveAxisWidth}
        min={min}
        max={max}
      />
    );
    annotationsDisplay = (
      <AnnotationsTimeDisplay
        scale={scale as ScaleTime<number, number>}
        tracks={annotationTracks}
        showLabels={false}
        labelsOffset={0}
        width={effectiveAxisWidth}
        markerGroupHover={handleMarkerGroupHover}
        tooltipConfig={tooltipConfig}
        annotationsActions={annotationsTooltipActions}
        textOverflow={textOverflow}
        truncateMode={truncateMode}
      />
    );
  }

  return (
    <DataTableDefaultCell
      ref={sizeRef}
      className={styles.ganttChartHeader}
      data-sizeref={true}
    >
      <Flex
        flexDirection="column"
        minHeight={annotationsHeightConfig}
        justifyContent="space-between"
        style={{ paddingLeft: leftHeaderPadding }}
      >
        {annotationsHeader && (
          <ChartStateHandler
            empty={isEmpty(annotationTracks)}
            height="100%"
            emptyState={emptyState}
            errorState={errorState}
            isLoading={loading ?? false}
            style={{ margin: 'auto' }}
          >
            <AnnotationsScrollWrapper
              height={scrollWrapperHeight}
              width={effectiveAxisWidth}
            >
              {annotationsDisplay}
            </AnnotationsScrollWrapper>
          </ChartStateHandler>
        )}
        {ganttAxis}
      </Flex>
    </DataTableDefaultCell>
  );
};
